'use client';

/**
 * Employee Portal Layout — themed via /v1/portal-settings/public.
 *
 * workspaceId resolution strategy (chosen: query-param + sessionStorage
 * fallback + authenticated recipient fallback):
 *
 *   1. First visit — link lands employee on `/portal/login?ws=<workspaceId>`.
 *      Layout reads the `ws` query param and persists it to
 *      `sessionStorage['hf-portal:ws-id']` so subsequent navigations keep
 *      the theme.
 *   2. Return visits — layout reads from `sessionStorage`.
 *   3. Authenticated recipient — if the portal JWT was already issued,
 *      `portalAuthApi.getRecipient().workspaceId` is the source of truth
 *      and is copied back into sessionStorage.
 *   4. No workspaceId known — layout renders default HF theme (portal is
 *      never fully blocked by theming failure).
 *
 * This file intentionally does NOT render <html>/<body> — the root
 * app/layout.tsx owns those and already wires QueryProvider, Toaster,
 * theme, etc. Nested layouts in Next.js App Router must delegate to the
 * root shell; otherwise hydration breaks + providers are duplicated.
 */

import { Suspense, useEffect, useMemo, useState } from 'react';
import { useSearchParams, usePathname } from 'next/navigation';
import {
  usePublicPortalSettings,
  usePublicPortalThemes,
} from '@/components/portal/hooks/use-portal-settings';
import { portalAuthApi, PORTAL_SESSION_EVENT } from '@/lib/api/portal-auth';
import type { PortalTheme } from '@/lib/types/portal-settings';
import { useTranslation } from '@/lib/i18n';
import { LanguageSwitcher } from '@/components/language-switcher';
import { portalCopy } from '@/components/user-portal/learner-i18n';
import { useLearnerLanguage } from '@/components/portal/hooks/use-learner-language';
import { AdminPreviewBanner } from './_components/admin-preview-banner';
import { CelebrationProvider } from './_components/celebrations/celebration-provider';
import { PortalSignOutButton } from './_components/portal-sign-out-button';
import { KnowledgeAskLauncher } from '@/components/knowledge/portal/ask-launcher';
import { KnowledgeLibraryLink } from '@/components/knowledge/portal/library-link';
import { KnowledgeAskAiButton } from '@/components/knowledge/portal/ask-ai-button';

const WS_STORAGE_KEY = 'hf-portal:ws-id';

// Canonical BE URL — single env var across the codebase. Drop the
// non-canonical `NEXT_PUBLIC_BE_ORIGIN` alias (never set in any .env).
const beOrigin =
  process.env.NEXT_PUBLIC_API_URL ?? 'http://localhost:4001';

// Mirrors the canonical Light theme palette (`modern-teal` seed @
// src/database/seed/portal-global/data/portal-themes.json) so a portal
// rendered without a resolved `workspaceId` (anonymous visit to
// /portal/login without `?ws=`, or a workspace whose settings 404)
// falls back to pure B&W instead of the legacy teal. Fixed 2026-05-14
// — the previous teal default (#065D66 / #0891B2) was leftover from
// the pre-d4105263 multi-theme era ("reduce to Light + Dark only,
// pure B&W (ticket #190)").
const DEFAULT_THEME = {
  primaryColor: '#000000',
  secondaryColor: '#525252',
  backgroundColor: '#ffffff',
  textColor: '#0a0a0a',
  headerColor: '#ffffff',
};

function useResolvedWorkspaceId(): string {
  const searchParams = useSearchParams();
  const [workspaceId, setWorkspaceId] = useState<string>('');

  useEffect(() => {
    if (typeof window === 'undefined') return;
    // 1. Query-param wins — overwrites storage so admins can re-theme by link.
    const fromQuery = searchParams?.get('ws') ?? '';
    if (fromQuery) {
      window.sessionStorage.setItem(WS_STORAGE_KEY, fromQuery);
      setWorkspaceId(fromQuery);
      return;
    }
    // 2. Session storage — set by previous visit.
    const fromStorage = window.sessionStorage.getItem(WS_STORAGE_KEY);
    if (fromStorage) {
      setWorkspaceId(fromStorage);
      return;
    }
    // 3. Authenticated recipient — copy into storage for next nav.
    const recipient = portalAuthApi.getRecipient();
    if (recipient?.workspaceId) {
      window.sessionStorage.setItem(WS_STORAGE_KEY, recipient.workspaceId);
      setWorkspaceId(recipient.workspaceId);
    }
  }, [searchParams]);

  return workspaceId;
}

function findTheme(
  themes: PortalTheme[] | undefined,
  themeId: string | null,
): PortalTheme | null {
  if (!themes || !themeId) return null;
  return themes.find((t) => t.id === themeId) ?? null;
}

export default function PortalLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  // useSearchParams triggers SSG bailout without a Suspense boundary.
  return (
    <Suspense fallback={<PortalLayoutFallback>{children}</PortalLayoutFallback>}>
      <PortalLayoutInner>{children}</PortalLayoutInner>
    </Suspense>
  );
}

function PortalLayoutFallback({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <div
      className="portal-shell min-h-screen"
      style={
        {
          '--portal-primary': DEFAULT_THEME.primaryColor,
          '--portal-secondary': DEFAULT_THEME.secondaryColor,
          '--portal-bg': DEFAULT_THEME.backgroundColor,
          '--portal-text': DEFAULT_THEME.textColor,
          '--portal-header': DEFAULT_THEME.headerColor,
          backgroundColor: DEFAULT_THEME.backgroundColor,
          color: DEFAULT_THEME.textColor,
        } as React.CSSProperties
      }
    >
      <main className="container max-w-5xl py-8">{children}</main>
    </div>
  );
}

function PortalLayoutInner({
  children,
}: {
  children: React.ReactNode;
}) {
  const workspaceId = useResolvedWorkspaceId();
  const pathname = usePathname();
  const { t } = useTranslation();
  // A first visit opens in the learner's own language (the directory's, which
  // every sign-in reports); a language chosen on this device always wins.
  useLearnerLanguage();
  // The dashboard route renders <PortalPreview> which owns its own header
  // + paints full-bleed. Suppress the layout chrome so we don't double up.
  // Subroutes (/portal/news, /portal/badges, etc.) keep the layout header
  // because they use the constrained <main> shell.
  const isDashboard = pathname === '/portal';
  // CelebrationProvider expects an employeeId for per-recipient dedup.
  // Recipient may not be hydrated on first render (e.g. login page) —
  // fall back to an empty string so the provider tree renders; it simply
  // won't fire events for anonymous sessions.
  const [recipientId, setRecipientId] = useState<string>('');
  // An admin "View as in Portal" session already has its own exit control in
  // AdminPreviewBanner, and its token is read-only chrome rather than a
  // learner's session — so the Sign out control below is hidden for it instead
  // of duplicating the banner's Exit (or 403ing against
  // PortalPreviewReadOnlyGuard).
  const [isAdminPreview, setIsAdminPreview] = useState(false);
  useEffect(() => {
    const readSession = () => {
      const recipient = portalAuthApi.getToken() ? portalAuthApi.getRecipient() : null;
      setRecipientId(recipient?.id ?? '');
      setIsAdminPreview(portalAuthApi.isAdminPreview());
    };
    readSession();
    window.addEventListener(PORTAL_SESSION_EVENT, readSession);
    window.addEventListener('storage', readSession);
    return () => {
      window.removeEventListener(PORTAL_SESSION_EVENT, readSession);
      window.removeEventListener('storage', readSession);
    };
  }, [pathname]);
  const {
    data: resolved,
    isLoading: settingsLoading,
    isError: settingsError,
  } = usePublicPortalSettings(workspaceId);

  // Themes fetched client-side (anon-safe variant) and joined with
  // settings.selectedTheme. Backend /v1/portal-themes/public resolves
  // the workspace's companyId server-side and returns global + company +
  // workspace themes without requiring a JWT.
  const { data: themes } = usePublicPortalThemes(workspaceId ?? '');

  const settings = resolved?.settings;
  const theme = useMemo(
    () => findTheme(themes, settings?.selectedTheme ?? null),
    [themes, settings?.selectedTheme],
  );

  const primary = theme?.primaryColor ?? DEFAULT_THEME.primaryColor;
  const secondary = theme?.secondaryColor ?? DEFAULT_THEME.secondaryColor;
  const background = theme?.backgroundColor ?? DEFAULT_THEME.backgroundColor;
  const text = theme?.textColor ?? DEFAULT_THEME.textColor;
  const header = theme?.headerColor ?? DEFAULT_THEME.headerColor;

  // The seeded heading is shown in the reader's language; one an admin wrote,
  // as written (portalCopy).
  const heroHeading = settings?.heroHeading
    ? portalCopy(t, 'heroHeading', settings.heroHeading)
    : t('portal.shell.defaultTitle');
  const useSameLogo = settings?.useSameLogoForBoth ?? true;
  const logo = useSameLogo
    ? settings?.portalLogoLight ?? null
    : // `light` is the conservative default — actual dark-mode swap belongs in a
      // future enhancement once the portal adopts next-themes in the (portal)
      // subtree. Keeping both images resolvable downstream.
      settings?.portalLogoLight ?? null;

  const isInitialLoading = !!workspaceId && settingsLoading;

  return (
    <>
      {/* Capture SDK hooks — previously in <head>; moved here because nested
          layouts can't render <head>. Next.js hoists <meta> tags inside body
          to the head at render time. */}
      <meta
        name="hfv3-capture-context"
        content='{"module":"LMS","targetId":"","surface":"portal-dashboard"}'
      />
      <meta name="hfv3-capture-api-base" content={beOrigin} />
      {/* TODO(capture): re-enable fingerprint.js once the BE adds
          Cross-Origin-Resource-Policy: cross-origin on /v1/capture/fingerprint.js.
          The portal does not ship a capture flow today — removing to eliminate
          the NotSameOrigin CORS error. */}

      <div
        // The admin app's global CSS pins `html, body { overflow: hidden;
        // height: 100vh }` (so the admin shell sidebar + chrome stay in
        // a fixed viewport). Inheriting that on the live portal blocks
        // scrolling for end-users — ticket #218. Following the same
        // workaround as `app/(portal-preview)/layout.tsx`: pin h-screen
        // + opt-in to vertical scroll via overflow-y-auto so the portal
        // gets its own scrollable region inside the admin overflow lock.
        // Fixed 2026-05-14.
        className="portal-shell h-screen w-screen overflow-y-auto overflow-x-hidden"
        style={
          {
            '--portal-primary': primary,
            '--portal-secondary': secondary,
            '--portal-bg': background,
            '--portal-text': text,
            '--portal-header': header,
            backgroundColor: background,
            color: text,
          } as React.CSSProperties
        }
      >
        {/* #417 — admin "View as in Portal" primes a real recipient session;
            this names whose account is on screen and offers the way out.
            Renders nothing for a genuine recipient login, and sits ABOVE
            the header so it is present on the dashboard too (which
            suppresses the layout chrome). */}
        <AdminPreviewBanner />

        {isDashboard && recipientId && !isAdminPreview ? (
          <div data-portal-bar="" className="flex items-center justify-end gap-4 border-b px-6 py-2 text-sm" style={{ backgroundColor: header, color: text }}>
            <KnowledgeLibraryLink className="hover:underline opacity-90" />
            <PortalSignOutButton />
          </div>
        ) : null}

        {!isDashboard && (
          <header
            data-portal-bar=""
            // Wraps on a phone: the brand above, the navigation under it
            // (it ran off the screen there, with Sign out out of reach).
            className="border-b px-4 sm:px-6 py-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-2"
            // text color must follow theme.textColor — hardcoded `#ffffff`
            // worked when headerColor was always dark (legacy teal default),
            // but Light theme now uses headerColor=#ffffff which made the
            // brand + nav text white-on-white invisible. Fixed 2026-05-14.
            style={{ backgroundColor: header, color: text }}
          >
            <div className="flex items-center gap-3">
              {logo ? (
                // Using a plain <img> so the public endpoint can ship any
                // http(s) URL without pre-configuring next/image domains.
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={logo}
                  alt={heroHeading}
                  className="h-8 w-auto max-w-[160px] object-contain"
                />
              ) : (
                <span className="font-semibold text-lg">HumanFirewall</span>
              )}
              <span className="hidden text-sm opacity-90 md:inline" dir="auto">{heroHeading}</span>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-x-4 gap-y-2">
              {/* Nav only renders once the recipient is authenticated. Before
                  login (e.g. on /portal/login itself), the nav links would
                  404 anyway since their routes require a portal JWT.
                  Fixed 2026-05-14 per product clarification. */}
              {recipientId ? (
                <nav className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                  <a href="/portal/training" className="hover:underline opacity-90">
                    {t('portal.shell.myTraining')}
                  </a>
                  <a
                    href="/portal/certificates"
                    className="hover:underline opacity-90"
                  >
                    {t('portal.shell.certificates')}
                  </a>
                  <KnowledgeLibraryLink className="hover:underline opacity-90" />
                  {!isAdminPreview ? <PortalSignOutButton /> : null}
                </nav>
              ) : null}
              {/* The policy assistant's "Ask AI" button, as the main app's
                  header has it; it shows only while an assistant is available. */}
              {recipientId ? (
                <KnowledgeAskAiButton />
              ) : null}
              {/* The global header controls are hidden on /portal, so the
                  portal carries its own switcher — signed in or not
                  (locale audit 2026-09-26, L-02). */}
              <LanguageSwitcher />
            </div>
          </header>
        )}

        <main
          className={
            isDashboard
              ? 'w-full' /* PortalPreview paints full-bleed */
              : 'container mx-auto max-w-5xl py-8'
          }
        >
          {isInitialLoading ? (
            <div
              className="flex items-center justify-center py-24"
              role="status"
              aria-live="polite"
            >
              <div
                className="h-10 w-10 animate-spin rounded-full border-4 border-current border-t-transparent opacity-60"
                style={{ color: primary }}
              />
              <span className="sr-only">{t('portal.shell.loading')}</span>
            </div>
          ) : (
            // Render children even when settingsError is true — theming
            // should NEVER block the portal. We fall through to DEFAULT_THEME
            // values already resolved above.
            <CelebrationProvider employeeId={recipientId}>
              {settingsError ? (
                <div className="sr-only" role="status">
                  {t('portal.shell.themeUnavailable')}
                </div>
              ) : null}
              {children}
              {/* The policy assistant (knowledge wave K1): a floating Ask
                  button on every signed-in portal page, when an assistant is
                  available to this person. */}
              {recipientId ? <KnowledgeAskLauncher /> : null}
            </CelebrationProvider>
          )}
        </main>
      </div>
    </>
  );
}
